/**
 * JSON-LD builders for Clamphook pages.
 *
 * Pass the return value of any builder to <SEO jsonLd={...} /> (or as an array)
 * and it'll be emitted as a properly framed application/ld+json block.
 *
 */

import type { JsonLd } from "lib/components/SEO";

const SITE_URL = "https://clamphook.com";
const ORG = {
  "@type": "EducationalOrganization",
  name: "Clamphook Academy",
  alternateName: "Clamphook",
  url: SITE_URL,
  logo: `${SITE_URL}/img/logo/App_Icon_Primary.png`,
  foundingDate: "2021",
  numberOfEmployees: {
    "@type": "QuantitativeValue",
    minValue: 30,
    maxValue: 50,
  },
  knowsAbout: [
    "IOE Entrance Preparation",
    "BSc CSIT Entrance Preparation",
    "NEB +2 Science",
    "Computer Based Test (CBT)",
    "Engineering Entrance Nepal",
    "Pulchowk Campus Admission",
    "Tribhuvan University Entrance",
  ],
  sameAs: [
    "https://www.facebook.com/clamphookioe/",
    "https://www.instagram.com/clamphookacademy/",
    "https://www.youtube.com/@clamphook",
    "https://apps.apple.com/us/app/clamphook-academy/id6737482974",
    "https://play.google.com/store/apps/details?id=com.clamphook.clamphook",
  ],
  address: {
    "@type": "PostalAddress",
    addressLocality: "Kathmandu",
    addressRegion: "Bagmati",
    addressCountry: "NP",
  },
};

export function organizationLd(): JsonLd {
  return ORG as JsonLd;
}

export function websiteLd(): JsonLd {
  return {
    "@type": "WebSite",
    name: "Clamphook Academy",
    url: SITE_URL,
    inLanguage: "en-NP",
    potentialAction: {
      "@type": "SearchAction",
      target: `${SITE_URL}/?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
}

export interface BreadcrumbItem {
  name: string;
  url: string;
}

export function breadcrumbLd(items: BreadcrumbItem[]): JsonLd {
  return {
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: item.url.startsWith("http") ? item.url : `${SITE_URL}${item.url}`,
    })),
  };
}

export interface CourseLdInput {
  name: string;
  description: string;
  url?: string;
  image?: string;
  price?: number | string;
  priceCurrency?: string;
  educationalLevel?: string;
  inLanguage?: string;
  /** Delivery modes offered, e.g. ["online", "in-person"]. Emits hasCourseInstance
   *  so the Course rich result satisfies Google's offers/hasCourseInstance requirement
   *  even when no fixed price is advertised. */
  courseModes?: Array<"online" | "in-person" | "blended">;
  aggregateRating?: AggregateRatingInput;
}

const COURSE_WORKLOAD_MAP: Record<string, string> = {
  online: "Online",
  "in-person": "Onsite",
  blended: "Blended",
};

export function courseLd(course: CourseLdInput): JsonLd {
  const offers =
    course.price !== undefined && course.price !== null
      ? {
          "@type": "Offer",
          price: String(course.price),
          priceCurrency: course.priceCurrency || "NPR",
          availability: "https://schema.org/InStock",
          url: course.url,
        }
      : undefined;

  const hasCourseInstance =
    !offers && course.courseModes?.length
      ? course.courseModes.map((mode) => ({
          "@type": "CourseInstance",
          courseMode: COURSE_WORKLOAD_MAP[mode] || mode,
          courseWorkload: "P6M",
        }))
      : undefined;

  return {
    "@type": "Course",
    name: course.name,
    description: course.description,
    provider: ORG,
    url: course.url,
    image: course.image,
    educationalLevel: course.educationalLevel,
    inLanguage: course.inLanguage || "en-NP",
    ...(offers ? { offers } : {}),
    ...(hasCourseInstance ? { hasCourseInstance } : {}),
    ...(course.aggregateRating
      ? { aggregateRating: aggregateRatingLd(course.aggregateRating) }
      : {}),
  };
}

export interface FAQItem {
  question: string;
  answer: string;
}

export function faqLd(items: FAQItem[]): JsonLd {
  return {
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: it.answer,
      },
    })),
  };
}

export interface ArticleLdInput {
  headline: string;
  description: string;
  image?: string;
  authorName?: string;
  publishedAt?: string;
  updatedAt?: string;
  url?: string;
}

export function articleLd(article: ArticleLdInput): JsonLd {
  return {
    "@type": "Article",
    headline: article.headline,
    description: article.description,
    image: article.image,
    datePublished: article.publishedAt,
    dateModified: article.updatedAt || article.publishedAt,
    author: {
      "@type": "Person",
      name: article.authorName || "Clamphook Academy",
    },
    publisher: ORG,
    mainEntityOfPage: article.url,
  };
}

// ── LocalBusiness ─────────────────────────────────────────────────────────────
// Enables the Google local knowledge panel and map pack entries.
export function localBusinessLd(): JsonLd {
  return {
    "@type": ["EducationalOrganization", "LocalBusiness"],
    name: "Clamphook Academy",
    alternateName: "Clamphook",
    description:
      "Nepal's #1 IOE & CSIT entrance preparation institute. Weekly mock tests, expert mentors, online + physical classes in Kathmandu.",
    url: SITE_URL,
    logo: `${SITE_URL}/img/logo/App_Icon_Primary.png`,
    image: `${SITE_URL}/img/Clamphook.png`,
    telephone: "+977-9851051663",
    email: "clamphook.academy@gmail.com",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Thapathali",
      addressLocality: "Kathmandu",
      addressRegion: "Bagmati",
      addressCountry: "NP",
    },
    // Coordinates of the Clamphook Academy Google Maps place; keep in sync
    // with hasMap below and the Google Business Profile.
    geo: {
      "@type": "GeoCoordinates",
      latitude: "27.6920174",
      longitude: "85.3183209",
    },
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: [
          "Sunday",
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
        ],
        opens: "06:00",
        closes: "22:00",
      },
    ],
    sameAs: [
      "https://www.facebook.com/clamphookioe/",
      "https://www.instagram.com/clamphookacademy/",
      "https://www.youtube.com/@clamphook",
    ],
    // Stable CID form of the Clamphook Academy place (0x47d1006bc4369829).
    hasMap: "https://maps.google.com/?cid=5174917909678823465",
    priceRange: "NPR",
    currenciesAccepted: "NPR",
    paymentAccepted: "Cash, Bank Transfer, eSewa, Khalti",
    areaServed: {
      "@type": "Country",
      name: "Nepal",
    },
  };
}

// ── HowTo ─────────────────────────────────────────────────────────────────────
// Enables how-to rich results with step-by-step instructions.
export interface HowToStep {
  name: string;
  text: string;
  url?: string;
}

export interface HowToLdInput {
  name: string;
  description: string;
  image?: string;
  totalTime?: string; // ISO 8601 duration e.g. "PT6M"
  steps: HowToStep[];
}

export function howToLd(input: HowToLdInput): JsonLd {
  return {
    "@type": "HowTo",
    name: input.name,
    description: input.description,
    image: input.image,
    totalTime: input.totalTime,
    step: input.steps.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.name,
      text: s.text,
      url: s.url,
    })),
  };
}

// ── ItemList ──────────────────────────────────────────────────────────────────
// Enables list rich results (e.g. comparison tables, top-N lists).
export interface ItemListEntry {
  name: string;
  url: string;
  description?: string;
  image?: string;
}

export function itemListLd(name: string, items: ItemListEntry[]): JsonLd {
  return {
    "@type": "ItemList",
    name,
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      url: item.url.startsWith("http") ? item.url : `${SITE_URL}${item.url}`,
      description: item.description,
      image: item.image,
    })),
  };
}

// ── AggregateRating ──────────────────────────────────────────────────────────
// Enables star-rating rich results on Course / Organization pages.
export interface AggregateRatingInput {
  ratingValue: number;
  bestRating?: number;
  ratingCount: number;
  reviewCount?: number;
}

export function aggregateRatingLd(
  input: AggregateRatingInput,
): Record<string, unknown> {
  return {
    "@type": "AggregateRating",
    ratingValue: String(input.ratingValue),
    bestRating: String(input.bestRating ?? 5),
    ratingCount: String(input.ratingCount),
    ...(input.reviewCount ? { reviewCount: String(input.reviewCount) } : {}),
  };
}

// ── VideoObject ──────────────────────────────────────────────────────────────
// Enables video rich results and video carousels.
export interface VideoObjectInput {
  name: string;
  description: string;
  thumbnailUrl: string;
  uploadDate: string;
  contentUrl?: string;
  embedUrl?: string;
  duration?: string; // ISO 8601 e.g. "PT10M30S"
}

export function videoObjectLd(video: VideoObjectInput): JsonLd {
  return {
    "@type": "VideoObject",
    name: video.name,
    description: video.description,
    thumbnailUrl: video.thumbnailUrl,
    uploadDate: video.uploadDate,
    contentUrl: video.contentUrl,
    embedUrl: video.embedUrl,
    duration: video.duration,
    publisher: ORG,
  };
}

// ── SoftwareApplication ─────────────────────────────────────────────────────
// Enables app pack / software rich results for the Clamphook mobile app.
export function softwareApplicationLd(): JsonLd {
  return {
    "@type": "SoftwareApplication",
    name: "Clamphook Academy",
    operatingSystem: "Android, iOS",
    applicationCategory: "EducationalApplication",
    description:
      "Nepal's best IOE & CSIT entrance preparation app. 200+ topic-wise CBT tests, live classes, a built-in study planner, and weekly mock exams.",
    url: SITE_URL,
    downloadUrl: [
      "https://play.google.com/store/apps/details?id=com.clamphook.clamphook",
      "https://apps.apple.com/us/app/clamphook-academy/id6737482974",
    ],
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "NPR",
    },
    // No aggregateRating: the only real ratings live on the Play Store and
    // App Store, and Google's review-snippet rules forbid marking up ratings
    // collected on another site.
    author: ORG,
  };
}

// ── EducationalOccupationalProgram ───────────────────────────────────────────
// Deeper entity mapping for IOE/CSIT entrance preparation programs.
export interface EducationalProgramInput {
  name: string;
  description: string;
  url?: string;
  timeToComplete?: string; // ISO 8601 duration e.g. "P6M"
  educationalProgramMode?: string; // "Online", "Onsite", "Blended"
  occupationalCategory?: string;
}

export function educationalProgramLd(input: EducationalProgramInput): JsonLd {
  return {
    "@type": "EducationalOccupationalProgram",
    name: input.name,
    description: input.description,
    url: input.url,
    provider: ORG,
    timeToComplete: input.timeToComplete,
    educationalProgramMode: input.educationalProgramMode,
    occupationalCategory: input.occupationalCategory,
    offers: {
      "@type": "Offer",
      category: "Paid",
    },
  };
}

// ── Person (Tutor / Mentor) ──────────────────────────────────────────────────
// Improves E-E-A-T by linking instructors to courses and the organization.
export interface PersonLdInput {
  name: string;
  jobTitle?: string;
  description?: string;
  image?: string;
  url?: string;
  alumniOf?: string;
  knowsAbout?: string[];
}

export function personLd(person: PersonLdInput): JsonLd {
  return {
    "@type": "Person",
    name: person.name,
    jobTitle: person.jobTitle || "IOE Entrance Mentor",
    description: person.description,
    image: person.image,
    url: person.url,
    worksFor: ORG,
    alumniOf: person.alumniOf
      ? {
          "@type": "EducationalOrganization",
          name: person.alumniOf,
        }
      : undefined,
    knowsAbout: person.knowsAbout,
  };
}

// Pre-defined mentor list for E-E-A-T structured data injection.
// These can be embedded in course pages and the team page.
export function mentorTeamLd(): JsonLd[] {
  const mentors: PersonLdInput[] = [
    {
      name: "Saksham",
      jobTitle: "IOE Physics & Mathematics Mentor",
      alumniOf: "IOE Pulchowk Campus",
      knowsAbout: [
        "IOE Entrance Preparation",
        "Engineering Physics",
        "Engineering Mathematics",
      ],
    },
    {
      name: "Sandip Gewali",
      jobTitle: "IOE Chemistry & English Mentor",
      alumniOf: "IOE Pulchowk Campus",
      knowsAbout: ["IOE Entrance Preparation", "Chemistry", "English for IOE"],
    },
    {
      name: "Nishant Shah",
      jobTitle: "Founder & Lead Mentor",
      alumniOf: "IOE Pulchowk Campus",
      knowsAbout: ["IOE Entrance Preparation", "BSc CSIT Entrance", "EdTech"],
    },
  ];

  return mentors.map((m) => personLd(m));
}
